import { CACHE_TIERS } from "@workspace/types";
import { trpc } from "@/lib/trpc/react";
import { useFestivalSets } from "../use-festival-details";

// Hook for festival details and stages
export function useFestivalWithStages(
	festivalSlug: string,
	eventType: "main_festival" | "pre_party" | "after_party" = "main_festival",
): ReturnType<typeof trpc.festival.getBySlug.useQuery> {
	return trpc.festival.getBySlug.useQuery(
		{ slug: festivalSlug, eventType },
		{
			...CACHE_TIERS.STATIC, // Festival details rarely change
			retry: (failureCount, error) => {
				// Don't retry if festival not found
				if (error && typeof error === "object" && "data" in error) {
					const data = error.data as any;
					if (data?.code === "NOT_FOUND") return false;
				}
				return failureCount < 2;
			},
		},
	);
}

// Hook for user festival data
export function useUserFestivalData(
	userId: string,
	festivalId: string | null,
	eventType: "main_festival" | "pre_party" | "after_party" = "main_festival",
): ReturnType<typeof trpc.festival.getUserFestivalData.useQuery> {
	return trpc.festival.getUserFestivalData.useQuery(
		{ userId, festivalId: festivalId!, eventType },
		{
			enabled: !!festivalId,
			...CACHE_TIERS.REALTIME, // staleTime: 0 means always refetch, but shows cached data instantly
			refetchOnWindowFocus: true,
			refetchOnMount: "always", // Always fetch fresh data on mount
			refetchOnReconnect: true,
			retry: (failureCount, error) => {
				// Don't retry schema/critical errors
				if (error && typeof error === "object" && "message" in error) {
					const errorMessage = error.message as string;
					if (
						errorMessage.includes("Schema") ||
						errorMessage.includes("critical")
					) {
						return false;
					}
				}
				return failureCount < 2;
			},
		},
	);
}

// Combined hook for all festival page data
export function useFestivalPageData(
	userId: string,
	festivalSlug: string,
	eventType: "main_festival" | "pre_party" | "after_party" = "main_festival",
): {
	festival: ReturnType<typeof useFestivalWithStages>;
	sets: ReturnType<typeof useFestivalSets>;
	user: ReturnType<typeof useUserFestivalData>;
	isLoading: boolean;
	hasError: boolean;
	error: any;
} {
	const festivalQuery = useFestivalWithStages(festivalSlug, eventType);

	// Use the festival ID from the first query for dependent queries
	const typedData = festivalQuery.data as
		| { festival?: { id?: string } }
		| undefined;
	const festivalId = typedData?.festival?.id;

	const setsQuery = useFestivalSets(festivalId || null, eventType);
	const userQuery = useUserFestivalData(userId, festivalId || null, eventType);

	return {
		festival: festivalQuery,
		sets: setsQuery,
		user: userQuery,
		isLoading: festivalQuery.isLoading || setsQuery.isLoading, // Don't include user query loading
		hasError: festivalQuery.isError || setsQuery.isError || userQuery.isError,
		error: festivalQuery.error || setsQuery.error || userQuery.error,
	};
}
